import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Button,
  Row,
  Alert,
} from "reactstrap";
import { P } from "../../../AbstractElements";
import { Product, ProductStatus } from "../../../Types/ProductManagement";
import { usePayments } from "../../../hooks/usePayment";
import AlertModal from "./AlertModal";
import { useProductRequest } from "../../../hooks/useProductRequest";
import { format } from "date-fns";
type Props = {
  product: Product;
};

function ProductByIdContainer({ product }: Props) {
  const { sendProductPaymentLink, loading, error } = usePayments();
  const {
    updateRequestStatus,
    loading: productRequestLoading,
    error: productError,
  } = useProductRequest();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertAction, setAlertAction] = useState<() => void>(() => {});
  const [showReasonInput, setShowReasonInput] = useState(false);
  const toggleAlert = () => setIsAlertOpen(false);

  const showAlert = (
    message: string,
    action: (reason?: string) => void,
    requireReason: boolean = false
  ) => {
    setAlertMessage(message);
    setAlertAction(() => action);
    setShowReasonInput(requireReason);
    setIsAlertOpen(true);
  };
  const handleSendAdvancePaymentLink = async () => {
    showAlert(
      "Are you sure you want to send the advance payment link?",
      async () => {
        try {
          const result = await sendProductPaymentLink(product.id, "ADVANCE");
          if (result) {
            setSuccessMessage("Advance payment link sent successfully!");
            await updateRequestStatus(
              ProductStatus.ADVANCED_PAYMENT_LINK_SENT,
              product.id
            );
            window.location.reload();
          }
        } catch (err) {
          console.error("Error sending advance payment link:", err);
        } finally {
          toggleAlert();
        }
      }
    );
  };

  const handleSendBalancePaymentLink = async () => {
    showAlert(
      "Are you sure you want to send the balance payment link?",
      async () => {
        try {
          const result = await sendProductPaymentLink(product.id, "BALANCE");
          if (result) {
            setSuccessMessage("Balance payment link sent successfully!");
            await updateRequestStatus(
              ProductStatus.PENDING_BALANCED_PAYMENT,
              product.id
            );
            window.location.reload();
          }
        } catch (err) {
          console.error("Error sending balance payment link:", err);
        } finally {
          toggleAlert();
        }
      }
    );
  };

  const handleDenyRequest = () => {
    showAlert(
      "Are you sure you want to deny this request?",
      async (reason?: string) => {
        try {
          await updateRequestStatus(ProductStatus.DENIED, product.id, reason);
          setSuccessMessage("Request denied successfully.");
          window.location.reload();
        } catch (error) {
          console.error("Error denying request:", error);
        } finally {
          toggleAlert();
        }
      },
      true
    );
  };

  const handleAdvancePaymentReceived = () => {
    showAlert("Confirm that advance payment has been received?", async () => {
      try {
        await updateRequestStatus(
          ProductStatus.ADVANCED_PAYMENT_CONFIRM,
          product.id
        );
        setSuccessMessage(
          "Advance payment confirmed. Ready for balance payment."
        );
        window.location.reload();
      } catch (error) {
        console.error("Error confirming advance payment:", error);
      } finally {
        toggleAlert();
      }
    });
  };

  const handleNoAdvancePayment = () => {
    showAlert(
      "Confirm that no advance payment was received?",
      async (reason?: string) => {
        try {
          await updateRequestStatus(
            ProductStatus.DENIED_ADVANCED_PAYMENT,
            product.id,
            reason
          );
          setSuccessMessage("Request denied due to no advance payment.");
          window.location.reload();
        } catch (error) {
          console.error("Error handling no advance payment:", error);
        } finally {
          toggleAlert();
        }
      },
      true
    );
  };

  const handleBalancePaymentReceived = () => {
    showAlert("Confirm that balance payment has been received?", async () => {
      try {
        await updateRequestStatus(
          ProductStatus.BALANCE_PAYMENT_CONFIRM,
          product.id
        );
        setSuccessMessage(
          "Balance payment confirmed. Rental ready for completion."
        );
        window.location.reload();
      } catch (error) {
        console.error("Error confirming balance payment:", error);
      } finally {
        toggleAlert();
      }
    });
  };

  const handleNoBalancePayment = () => {
    showAlert(
      "Confirm that no balance payment was received?",
      async (reason?: string) => {
        try {
          await updateRequestStatus(
            ProductStatus.BALANCE_PAYMENT_DENIED,
            product.id,
            reason
          );
          setSuccessMessage(
            "Balance payment denied. Further action may be required."
          );
          window.location.reload();
        } catch (error) {
          console.error("Error handling no balance payment:", error);
        } finally {
          toggleAlert();
        }
      },
      true
    );
  };
  const handleComplete = () => {
    showAlert(
      "Are you sure you want to mark this rental as complete?",
      async () => {
        try {
          await updateRequestStatus(ProductStatus.COMPLETE, product.id);
          setSuccessMessage("Rental marked as complete.");
          window.location.reload();
        } catch (error) {
          console.error("Error completing rental:", error);
        } finally {
          toggleAlert();
        }
      }
    );
  };

  const handleCancel = () => {
    showAlert("Are you sure you want to cancel this rental?", async () => {
      try {
        await updateRequestStatus(ProductStatus.CANCELLED, product.id);
        setSuccessMessage("Rental cancelled successfully.");
        window.location.reload();
      } catch (error) {
        console.error("Error cancelling rental:", error);
      } finally {
        toggleAlert();
      }
    });
  };

  const renderActionButtons = () => {
    switch (product.status) {
      case ProductStatus.NEW:
      case ProductStatus.PENDING:
        return (
          <Row>
            <Col sm="6" md="6">
              <Button disabled={loading} onClick={handleSendAdvancePaymentLink}>
                Send Advance Payment Link
              </Button>
            </Col>
            <Col sm="6" md="6">
              <Button
                disabled={productRequestLoading}
                onClick={handleDenyRequest}
              >
                Deny
              </Button>
            </Col>
          </Row>
        );
      case ProductStatus.ADVANCED_PAYMENT_LINK_SENT:
        console.log(product.status);
        return (
          <Row>
            <Col sm="6" md="6">
              <Button
                disabled={productRequestLoading}
                onClick={handleAdvancePaymentReceived}
              >
                Advance Payment Received
              </Button>
            </Col>
            <Col sm="6" md="6">
              <Button
                disabled={productRequestLoading}
                onClick={handleNoAdvancePayment}
              >
                No Payment
              </Button>
            </Col>
          </Row>
        );
      case ProductStatus.ADVANCED_PAYMENT_CONFIRM:
        return (
          <Row>
            <Col sm="12" md="12">
              <Button disabled={loading} onClick={handleSendBalancePaymentLink}>
                Send Balance Payment Link
              </Button>
            </Col>
          </Row>
        );
      case ProductStatus.PENDING_BALANCED_PAYMENT:
        return (
          <Row>
            <Col sm="6" md="6">
              <Button
                disabled={productRequestLoading}
                onClick={handleBalancePaymentReceived}
              >
                Balance Payment Received
              </Button>
            </Col>
            <Col sm="6" md="6">
              <Button
                disabled={productRequestLoading}
                onClick={handleNoBalancePayment}
              >
                No Balance Payment
              </Button>
            </Col>
          </Row>
        );
      case ProductStatus.BALANCE_PAYMENT_CONFIRM:
        return (
          <Row>
            <Col sm="12" md="12">
              <Button disabled={productRequestLoading} onClick={handleComplete}>
                Complete Rental
              </Button>
            </Col>
          </Row>
        );
      case ProductStatus.DENIED:
      case ProductStatus.DENIED_ADVANCED_PAYMENT:
      case ProductStatus.BALANCE_PAYMENT_DENIED:
      case ProductStatus.COMPLETE:
      case ProductStatus.CANCELLED:
        return null;
      default:
        return (
          <Row>
            <Col sm="12" md="12">
              <Button disabled={productRequestLoading} onClick={handleCancel}>
                Cancel Rental
              </Button>
            </Col>
          </Row>
        );
    }
  };

  return (
    <Col>
      <Row>
        <Form>
          <Card>
            <CardBody>
              <Row>
                <Col md={12} className="mb-3">
                  <P className={"card-title mb-0"}>Rental Product Management</P>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>{"Request Id"}</Label>
                    <Input type="text" value={product?.id} disabled />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>{"Customer Name"}</Label>
                    <Input
                      type="text"
                      value={`${product.firstName} ${product.lastName}`}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>{"Request Status"}</Label>
                    <Input type="text" value={product.status} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Customer Contact Number"}</Label>
                    <Input type="text" value={product.contactNumber} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Customer Email"}</Label>
                    <Input type="text" value={product.email} disabled />
                  </FormGroup>
                </Col>

                <h5 style={{ marginTop: 30 }}>Address</h5>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Unit Number"}</Label>
                    <Input type="text" value={product.unitNumber} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Street Number"}</Label>
                    <Input type="text" value={product.streetNumber} disabled />
                  </FormGroup>
                </Col>

                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Street Name"}</Label>
                    <Input type="text" value={product.streetName} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Suburb"}</Label>
                    <Input type="text" value={product.suburb} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"VIC Postal Code"}</Label>
                    <Input type="text" value={product.vicPostalCode} disabled />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6" style={{ marginBottom: 40 }}>
                  <FormGroup>
                    <Label>{"State"}</Label>
                    <Input type="text" value={product.state} disabled />
                  </FormGroup>
                </Col>

                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Product Name"}</Label>
                    <Input
                      type="text"
                      value={product.productType.name}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Rental Start Date"}</Label>
                    <Input
                      type="text"
                      value={format(
                        new Date(product.rentalStartDate),
                        "yyyy/MM/dd"
                      )}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>{"Rental End Date"}</Label>
                    <Input
                      type="text"
                      value={format(
                        new Date(product.rentalEndDate),
                        "yyyy/MM/dd"
                      )}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm="12" md="12">
                  <FormGroup>
                    <Label>{"Message"}</Label>
                    <Input
                      type="textarea"
                      placeholder="No Message"
                      value={product.message}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </Row>
      {error && <Alert color="danger">{error}</Alert>}
      {productError && <Alert color="danger">{productError}</Alert>}
      {successMessage && <Alert color="success">{successMessage}</Alert>}
      {renderActionButtons()}
      <AlertModal
        isOpen={isAlertOpen}
        toggle={toggleAlert}
        message={alertMessage}
        onConfirm={alertAction}
        onCancel={toggleAlert}
        showReasonInput={showReasonInput}
      />
    </Col>
  );
}

export default ProductByIdContainer;
